import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { UserAvatar } from "@/components/TaskShared";
import { SavedViews } from "@/components/SavedViews";
import { toast } from "sonner";
import { Loader2, Clock, Gauge, ListChecks, AlertTriangle, CheckCircle2, Wallet, TrendingUp, Users2, Activity, LogIn, MonitorPlay, MessageSquare, PlusCircle, UserCheck, PenLine } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const money = (n) => `$${Number(n || 0).toLocaleString("en-AU", { maximumFractionDigits: 0 })}`;
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const fmtDur = (ms) => {
  const m = Math.round((ms || 0) / 60000);
  if (!m) return "—";
  const h = Math.floor(m / 60), r = m % 60;
  return h ? `${h}h ${r}m` : `${r}m`;
};
const fmtMin = (min) => { if (!min) return "—"; const h = Math.floor(min / 60), r = min % 60; return h ? `${h}h ${r}m` : `${r}m`; };
const fmtAgo = (isoStr) => {
  if (!isoStr) return "Never";
  const then = new Date(isoStr).getTime(); if (isNaN(then)) return "—";
  const s = Math.floor((Date.now() - then) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); if (d < 30) return `${d}d ago`;
  return new Date(isoStr).toLocaleDateString("en-AU", { day: "numeric", month: "short" });
};

function presetRange(key) {
  const now = new Date(); now.setHours(0, 0, 0, 0);
  if (key === "month") {
    const s = new Date(now.getFullYear(), now.getMonth(), 1);
    const e = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { start: iso(s), end: iso(e) };
  }
  if (key === "4w") {
    const s = new Date(now); s.setDate(s.getDate() - 27);
    return { start: iso(s), end: iso(now) };
  }
  const dow = (now.getDay() + 6) % 7; // Mon=0
  const s = new Date(now); s.setDate(s.getDate() - dow);
  const e = new Date(s); e.setDate(e.getDate() + 6);
  return { start: iso(s), end: iso(e) };
}
const PRESETS = [{ key: "week", label: "This week" }, { key: "month", label: "This month" }, { key: "4w", label: "Last 4 weeks" }];

export default function ConsoleInsights() {
  const [tab, setTab] = useState("delivery");
  const [preset, setPreset] = useState("week");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const range = useMemo(() => presetRange(preset), [preset]);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/console/insights?start=${range.start}&end=${range.end}`); setData(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLoading(false); }
  };
  useEffect(() => { if (tab === "delivery") load(); /* eslint-disable-next-line */ }, [preset, tab]);

  const h = data?.health || {};
  return (
    <main className="px-6 md:px-10 py-10 max-w-6xl mx-auto" data-testid="console-insights-page">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#06060C]">Insights</h1>
          <p className="mt-2 text-sm text-[#9FA1A4]">{tab === "delivery" ? "Time logged, utilisation vs capacity and per-client margins." : "How your agency team is using the platform."}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <SavedViews screen="insights" state={{ tab, preset }} defaultState={{ tab: "delivery", preset: "week" }} onApply={(s) => { setTab(s?.tab || "delivery"); setPreset(s?.preset || "week"); }} />
          <div className="flex items-center gap-1 rounded-full border border-[#EEEFEF] bg-white p-1" data-testid="insights-period">
            {PRESETS.map((p) => (
              <button key={p.key} data-testid={`insights-preset-${p.key}`} onClick={() => setPreset(p.key)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors ${preset === p.key ? "bg-[#06060C] text-white" : "text-[#595D62] hover:bg-[#F6F6FD]"}`}>{p.label}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-1 border-b border-[#EEEFEF] mb-8">
        {[{ k: "delivery", label: "Delivery" }, { k: "platform", label: "Agency Platform" }].map((t) => (
          <button key={t.k} data-testid={`insights-tab-${t.k}`} onClick={() => setTab(t.k)}
            className={`px-4 py-2.5 text-sm font-medium -mb-px border-b-2 transition-colors ${tab === t.k ? "border-[#723FFF] text-[#06060C]" : "border-transparent text-[#9FA1A4] hover:text-[#595D62]"}`}>{t.label}</button>
        ))}
      </div>

      {tab === "platform" ? <PlatformInsights range={range} /> : (
      loading || !data ? (
        <div className="py-24 text-center"><Loader2 className="h-5 w-5 animate-spin text-[#c4c5cc] mx-auto" /></div>
      ) : (
        <div className="space-y-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="insights-health">
            <Kpi icon={Clock} label="Hours logged" value={`${h.total_logged_hours ?? 0}h`} accent="#723FFF" />
            <Kpi icon={Gauge} label="Utilisation" value={h.overall_utilization_pct != null ? `${h.overall_utilization_pct}%` : "-"} accent="#30BD79" tone={utilTone(h.overall_utilization_pct)} />
            <Kpi icon={ListChecks} label="Active tasks" value={h.active_tasks ?? 0} accent="#0ea5e9" />
            <Kpi icon={AlertTriangle} label="Overdue" value={h.overdue_tasks ?? 0} accent={h.overdue_tasks ? "#F95B5F" : "#9FA1A4"} />
            <Kpi icon={CheckCircle2} label="Completed" value={h.completed_tasks ?? 0} accent="#30BD79" />
            <Kpi icon={Wallet} label="Retainers /mo" value={money(h.monthly_retainers)} accent="#06060C" />
            <Kpi icon={TrendingUp} label="Cost to service" value={money(h.total_cost ?? h.logged_cost)} sub={`${money(h.logged_cost)} time + ${money(h.additional_costs)} extra`} accent="#F9C509" />
            {h.actual_cost != null && <Kpi icon={Wallet} label="Actual costs" value={money(h.actual_cost)} sub={`excl. ${money(h.agency_overhead)} agency overhead`} accent="#E5484D" />}
            <Kpi icon={Users2} label="Clients" value={`${h.active_clients ?? 0}`} sub={`${h.active_projects ?? 0} projects`} accent="#723FFF" />
          </div>

          <section className="rounded-3xl border border-[#EEEFEF] premium-shadow bg-white p-6" data-testid="insights-utilization">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-lg font-semibold text-[#06060C]">Utilisation vs capacity</h2>
              <span className="text-xs text-[#9FA1A4]">{range.start} to {range.end}</span>
            </div>
            {data.utilization.length === 0 ? (
              <p className="text-sm text-[#9FA1A4]">No time logged in this period. Set capacity/rates in Team settings and log time on tasks.</p>
            ) : (
              <div className="space-y-4">
                {data.utilization.map((u) => {
                  const pct = u.utilization_pct;
                  const barW = pct == null ? 0 : Math.min(100, pct);
                  const color = pct == null ? "#c4c5cc" : pct > 100 ? "#F95B5F" : pct >= 70 ? "#30BD79" : "#723FFF";
                  return (
                    <div key={u.id} data-testid={`insights-util-${u.id}`} className="flex items-center gap-4">
                      <div className="flex items-center gap-2.5 w-52 shrink-0 min-w-0">
                        <UserAvatar name={u.name} uid={u.id} hasAvatar={u.has_avatar} size={30} />
                        <div className="min-w-0"><p className="text-sm text-[#06060C] truncate">{u.name}</p><p className="text-[11px] text-[#9FA1A4] truncate">{u.department || "No department"}</p></div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="h-3 rounded-full bg-[#F1F1F5] overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${barW}%`, background: color }} /></div>
                        <p className="text-[11px] text-[#9FA1A4] mt-1 font-mono-data">{u.logged_hours}h / {u.capacity_period_hours}h cap{u.billable_value ? ` · ${money(u.billable_value)} billable` : ""}</p>
                      </div>
                      <div className="w-16 text-right shrink-0"><span className="font-display text-base font-semibold" style={{ color }}>{pct == null ? "-" : `${pct}%`}</span></div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <section className="rounded-3xl border border-[#EEEFEF] premium-shadow bg-white overflow-hidden" data-testid="insights-margins">
            <div className="p-6 pb-3 flex items-center justify-between">
              <h2 className="font-display text-lg font-semibold text-[#06060C]">Client margins</h2>
              <span className="text-xs text-[#9FA1A4]">Retainer and additional costs prorated to period</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-y border-[#f0f0f3] text-left font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4]">
                  <th className="px-6 py-2.5">Client</th><th className="px-4 py-2.5 text-right">Hours</th>
                  <th className="px-4 py-2.5 text-right">Retainer</th><th className="px-4 py-2.5 text-right">Timesheet cost</th>
                  <th className="px-4 py-2.5 text-right">Additional</th><th className="px-4 py-2.5 text-right">Total cost</th>
                  <th className="px-4 py-2.5 text-right">Margin</th>
                  {h.actual_cost != null && <><th className="px-4 py-2.5 text-right">Actual costs</th><th className="px-6 py-2.5 text-right">Actual margin</th></>}
                </tr></thead>
                <tbody>
                  {data.clients.map((c) => {
                    const mp = c.margin_pct;
                    const mColor = c.margin < 0 ? "#F95B5F" : mp != null && mp >= 40 ? "#30BD79" : "#06060C";
                    return (
                      <tr key={c.id} data-testid={`insights-client-${c.id}`} className="border-b border-[#f6f6f9] hover:bg-[#FCFCFE]">
                        <td className="px-6 py-3"><div className="flex items-center gap-2.5"><span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: c.accent_color || "#723FFF" }} /><span className="text-[#06060C] truncate">{c.name}</span></div></td>
                        <td className="px-4 py-3 text-right font-mono-data text-[#595D62]">{c.logged_hours}h</td>
                        <td className="px-4 py-3 text-right font-mono-data text-[#595D62]">{c.retainer_window ? money(c.retainer_window) : "-"}</td>
                        <td className="px-4 py-3 text-right font-mono-data text-[#595D62]">{money(c.logged_cost)}</td>
                        <td className="px-4 py-3 text-right font-mono-data text-[#595D62]" data-testid={`insights-client-additional-${c.id}`}>{money(c.additional_cost)}</td>
                        <td className="px-4 py-3 text-right font-mono-data text-[#06060C]" data-testid={`insights-client-total-${c.id}`}>{money(c.total_cost)}</td>
                        <td className="px-4 py-3 text-right"><span className="font-mono-data font-semibold" style={{ color: mColor }}>{c.retainer_window ? money(c.margin) : "-"}</span>{mp != null && <span className="text-[11px] text-[#9FA1A4] ml-1.5">{mp}%</span>}</td>
                        {h.actual_cost != null && <>
                          <td className="px-4 py-3 text-right font-mono-data text-[#06060C]" data-testid={`insights-client-actual-${c.id}`} title={`Team share ${money(c.actual_team_cost)} + additional ${money(c.additional_cost)}`}>{money(c.actual_cost)}</td>
                          <td className="px-6 py-3 text-right" data-testid={`insights-client-actual-margin-${c.id}`}><span className="font-mono-data font-semibold" style={{ color: c.actual_margin < 0 ? "#F95B5F" : c.actual_margin_pct != null && c.actual_margin_pct >= 40 ? "#30BD79" : "#06060C" }}>{c.retainer_window ? money(c.actual_margin) : "-"}</span>{c.actual_margin_pct != null && <span className="text-[11px] text-[#9FA1A4] ml-1.5">{c.actual_margin_pct}%</span>}</td>
                        </>}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      ))}
    </main>
  );
}

function StatCell({ value }) { return <td className="px-3 py-3 text-right font-mono-data text-[#595D62]">{value}</td>; }

function PlatformInsights({ range }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState({ key: "last_seen_at", dir: "desc" });
  const [openUser, setOpenUser] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/console/insights/platform?start=${range.start}&end=${range.end}`)
      .then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [range.start, range.end]);

  const users = data?.users || [];
  const totals = useMemo(() => users.reduce((a, u) => ({
    loads: a.loads + u.loads, logins: a.logins + u.logins, active: a.active + u.active_ms,
    created: a.created + u.tasks_created, completed: a.completed + u.tasks_completed,
  }), { loads: 0, logins: 0, active: 0, created: 0, completed: 0 }), [users]);

  const sorted = useMemo(() => {
    const arr = [...users];
    const { key, dir } = sort; const mul = dir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      let av = a[key], bv = b[key];
      if (key === "last_seen_at" || key === "last_login_at") { av = av ? new Date(av).getTime() : 0; bv = bv ? new Date(bv).getTime() : 0; }
      if (typeof av === "string") return av.localeCompare(bv) * mul;
      return ((av || 0) - (bv || 0)) * mul;
    });
    return arr;
  }, [users, sort]);

  const maxModuleMs = useMemo(() => Math.max(1, ...users.flatMap((u) => (u.modules || []).map((m) => m.ms))), [users]);
  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));
  const Th = ({ k, children, align = "right" }) => (
    <th className={`px-3 py-2.5 text-${align} cursor-pointer select-none hover:text-[#06060C]`} onClick={() => toggleSort(k)} data-testid={`platform-sort-${k}`}>
      {children}{sort.key === k ? (sort.dir === "desc" ? " ↓" : " ↑") : ""}
    </th>
  );

  if (loading || !data) return <div className="py-24 text-center"><Loader2 className="h-5 w-5 animate-spin text-[#c4c5cc] mx-auto" /></div>;

  return (
    <div className="space-y-8" data-testid="insights-platform">
      <div className="rounded-2xl border border-[#EDE7FF] bg-[#F8F5FF] px-4 py-3 text-xs text-[#5b2fd6]">
        Usage tracking (logins, loads, time per module, last active) is recorded from when this feature went live — earlier sessions aren't included. Task metrics reflect all existing data.
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="platform-kpis">
        <Kpi icon={LogIn} label="Logins" value={totals.logins} accent="#723FFF" />
        <Kpi icon={MonitorPlay} label="Platform loads" value={totals.loads} accent="#0ea5e9" />
        <Kpi icon={Activity} label="Active time" value={fmtDur(totals.active)} accent="#30BD79" />
        <Kpi icon={PlusCircle} label="Tasks created" value={totals.created} accent="#F9C509" />
        <Kpi icon={CheckCircle2} label="Tasks completed" value={totals.completed} accent="#30BD79" />
      </div>

      <section className="rounded-3xl border border-[#EEEFEF] premium-shadow bg-white overflow-hidden">
        <div className="p-6 pb-3"><h2 className="font-display text-lg font-semibold text-[#06060C]">Team activity</h2><p className="text-xs text-[#9FA1A4] mt-0.5">{range.start} to {range.end} · click a column to sort</p></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-y border-[#f0f0f3] font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4]">
              <th className="px-6 py-2.5 text-left">Member</th>
              <Th k="last_seen_at" align="left">Last active</Th>
              <Th k="last_login_at" align="left">Last login</Th>
              <Th k="logins">Logins</Th>
              <Th k="loads">Loads</Th>
              <Th k="active_ms">Active</Th>
              <Th k="tasks_created">Created</Th>
              <Th k="tasks_assigned">Assigned</Th>
              <Th k="tasks_completed">Done</Th>
              <Th k="tasks_engaged">Engaged</Th>
              <Th k="comments">Comments</Th>
              <Th k="time_logged_min">Logged</Th>
            </tr></thead>
            <tbody>
              {sorted.map((u) => (
                <tr key={u.id} data-testid={`platform-row-${u.id}`} onClick={() => setOpenUser(u.id)} className="border-b border-[#f6f6f9] hover:bg-[#F8F5FF] cursor-pointer">
                  <td className="px-6 py-3"><div className="flex items-center gap-2.5 min-w-0"><UserAvatar name={u.name} uid={u.id} hasAvatar={u.has_avatar} size={30} /><div className="min-w-0"><p className="text-[#06060C] truncate hover:text-[#5b2fd6]">{u.name}</p><p className="text-[11px] text-[#9FA1A4] capitalize">{(u.role || "").replace("_", " ")}</p></div></div></td>
                  <td className="px-3 py-3 text-[#595D62] whitespace-nowrap">{fmtAgo(u.last_seen_at)}</td>
                  <td className="px-3 py-3 text-[#595D62] whitespace-nowrap">{fmtAgo(u.last_login_at)}</td>
                  <StatCell value={u.logins || "—"} />
                  <StatCell value={u.loads || "—"} />
                  <StatCell value={fmtDur(u.active_ms)} />
                  <StatCell value={u.tasks_created || "—"} />
                  <StatCell value={u.tasks_assigned || "—"} />
                  <StatCell value={u.tasks_completed || "—"} />
                  <StatCell value={u.tasks_engaged || "—"} />
                  <StatCell value={u.comments || "—"} />
                  <StatCell value={fmtMin(u.time_logged_min)} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-3xl border border-[#EEEFEF] premium-shadow bg-white p-6">
        <h2 className="font-display text-lg font-semibold text-[#06060C] mb-1">Time spent per module</h2>
        <p className="text-xs text-[#9FA1A4] mb-5">Where each teammate spends their time in the console.</p>
        <div className="space-y-5">
          {sorted.filter((u) => (u.modules || []).length).map((u) => (
            <div key={u.id} data-testid={`platform-modules-${u.id}`}>
              <div className="flex items-center gap-2 mb-2"><UserAvatar name={u.name} uid={u.id} hasAvatar={u.has_avatar} size={22} /><span className="text-sm text-[#06060C]">{u.name}</span><span className="text-[11px] text-[#9FA1A4] font-mono-data">· {fmtDur(u.active_ms)} total</span></div>
              <div className="space-y-1.5 pl-8">
                {u.modules.slice(0, 6).map((m) => (
                  <div key={m.module} className="flex items-center gap-3">
                    <span className="w-32 shrink-0 text-[11px] text-[#595D62] truncate">{m.label}</span>
                    <div className="flex-1 h-2.5 rounded-full bg-[#F1F1F5] overflow-hidden"><div className="h-full rounded-full bg-[#723FFF]" style={{ width: `${Math.max(4, (m.ms / maxModuleMs) * 100)}%` }} /></div>
                    <span className="w-16 text-right text-[11px] text-[#9FA1A4] font-mono-data shrink-0">{fmtDur(m.ms)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {!sorted.some((u) => (u.modules || []).length) && <p className="text-sm text-[#9FA1A4]">No module usage recorded yet for this period.</p>}
        </div>
      </section>

      {openUser && <PlatformUserModal userId={openUser} range={range} onClose={() => setOpenUser(null)} />}
    </div>
  );
}

const TL_ICON = {
  login: { icon: LogIn, color: "#723FFF" },
  load: { icon: MonitorPlay, color: "#0ea5e9" },
  module: { icon: Activity, color: "#30BD79" },
  task_created: { icon: PlusCircle, color: "#F59E0B" },
  task_comment: { icon: MessageSquare, color: "#5b2fd6" },
  task_time: { icon: Clock, color: "#0ea5e9" },
  task_status: { icon: CheckCircle2, color: "#30BD79" },
  task_edited: { icon: PenLine, color: "#9FA1A4" },
  task_assigned: { icon: UserCheck, color: "#723FFF" },
};
const dayLabel = (d) => {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const dt = new Date(d + "T00:00:00");
  const diff = Math.round((today - dt) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return dt.toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "short" });
};
const timeOfDay = (isoStr) => { try { return new Date(isoStr).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

function PlatformUserModal({ userId, range, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get(`/console/insights/platform/${userId}/timeline?start=${range.start}&end=${range.end}`)
      .then(({ data }) => setData(data)).catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); onClose(); });
  }, [userId, range.start, range.end]);

  const groups = useMemo(() => {
    const g = {};
    (data?.events || []).forEach((e) => { const day = (e.at || "").slice(0, 10); (g[day] = g[day] || []).push(e); });
    return Object.entries(g).sort((a, b) => b[0].localeCompare(a[0]));
  }, [data]);
  const u = data?.user;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white border-[#EEEFEF] text-[#06060C] max-w-none w-screen h-screen top-0 left-0 translate-x-0 translate-y-0 rounded-none flex flex-col gap-4 p-6 md:p-10" data-testid="platform-user-modal">
        {!data ? <div className="m-auto py-16"><DialogTitle className="sr-only">Activity</DialogTitle><Loader2 className="h-6 w-6 animate-spin text-[#c4c5cc]" /></div> : (
          <>
            <DialogHeader className="shrink-0">
              <div className="flex items-center gap-3">
                <UserAvatar name={u.name} uid={u.id} hasAvatar={u.has_avatar} size={44} />
                <div>
                  <DialogTitle className="font-display text-xl">{u.name}</DialogTitle>
                  <DialogDescription className="text-[#9FA1A4] capitalize">{(u.role || "").replace("_", " ")}{u.department ? ` · ${u.department}` : ""} · last active {fmtAgo(u.last_seen_at)}</DialogDescription>
                </div>
              </div>
            </DialogHeader>
            <p className="shrink-0 text-xs text-[#9FA1A4] -mt-1">Activity timeline · {range.start} to {range.end}</p>
            <div className="flex-1 min-h-0 overflow-y-auto max-w-3xl w-full">
              {groups.length === 0 ? (
                <div className="py-24 text-center text-sm text-[#9FA1A4]">No recorded activity in this period.</div>
              ) : groups.map(([day, evs]) => (
                <div key={day} className="mb-6" data-testid={`platform-tl-day-${day}`}>
                  <p className="font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4] mb-3 sticky top-0 bg-white py-1">{dayLabel(day)}</p>
                  <div className="relative pl-6 border-l border-[#EEEFEF] space-y-4">
                    {evs.map((e, i) => {
                      const cfg = TL_ICON[e.kind] || { icon: Activity, color: "#9FA1A4" };
                      const Icon = cfg.icon;
                      return (
                        <div key={i} data-testid="platform-tl-event" className="relative">
                          <span className="absolute -left-[31px] top-0 h-6 w-6 rounded-full flex items-center justify-center" style={{ background: `${cfg.color}18`, color: cfg.color }}><Icon className="h-3.5 w-3.5" /></span>
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <span className="text-sm text-[#06060C]">{e.label}</span>
                            {e.task && <span className="font-mono-data text-[11px] text-[#5b2fd6]">{e.task.code}</span>}
                            {e.detail && <span className="text-[11px] text-[#9FA1A4]">· {e.detail}</span>}
                            <span className="text-[11px] text-[#c4c5cc] font-mono-data ml-auto">{timeOfDay(e.at)}</span>
                          </div>
                          {e.task?.title && <p className="text-[11px] text-[#9FA1A4] truncate">{e.task.title}</p>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function utilTone(pct) { return pct == null ? "#9FA1A4" : pct > 100 ? "#F95B5F" : "#30BD79"; }

function Kpi({ icon: Icon, label, value, sub, accent = "#723FFF", tone }) {
  return (
    <div className="rounded-2xl border border-[#EEEFEF] bg-white p-4 premium-shadow">
      <div className="h-8 w-8 rounded-xl flex items-center justify-center mb-2.5" style={{ background: `${accent}14`, color: accent }}><Icon className="h-4 w-4" /></div>
      <p className="font-display text-2xl font-semibold" style={{ color: tone || "#06060C" }}>{value}</p>
      <p className="text-[11px] text-[#9FA1A4] mt-0.5">{label}{sub ? ` · ${sub}` : ""}</p>
    </div>
  );
}
